import * as React from 'react'
import { useRef, useCallback } from 'react'
import styled from 'styled-components'
import { ConversationMessage } from '../../types'
import Avatar from './Messenger/Avatar/Avatar'
import moment from '../../utilities/Moment'
import MessageContent from './MessageContent'

const CloseButton = styled.button`
  position: absolute;
  top: -8px;
  right: -8px;
  width: 20px;
  height: 20px;
  padding: 2px;
  border-radius: 50%;
  border: 1px solid rgb(237, 237, 237);
  background: rgb(255, 255, 255);
  box-shadow: rgba(9, 14, 21, 0.16) 0px 0px 4px 0px;
  cursor: pointer;
  display: flex;
  -webkit-box-align: center;
  align-items: center;
  -webkit-box-pack: center;
  justify-content: center;
  opacity: 0;
  transform: scale(0.8);
  transition: 0.2s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    background: rgb(245, 245, 245);
  }

  svg {
    width: 11px;
    height: 11px;
    color: rgb(108, 111, 116);
  }
`

const Container = styled.div`
  font-family: system-ui, "Open Sans", Roboto, Helvetica, Arial, sans-serif, "Apple Color Emoji";
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
  width: 100%;
  box-sizing: border-box;
  padding-right: 8px;

  &:hover {
    ${CloseButton} {
      opacity: 1;
      transform: scale(1);
    }
  }
`

const Preview = styled.div`
  background: white;
  border-radius: 12px;
  padding: 16px;
  display: flex;
  gap: 12px;
  align-items: flex-start;
  cursor: pointer;
  width: 100%;
  box-sizing: border-box;
  position: relative;
  border: 1px solid rgb(229, 229, 229);
`

const MessageContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  padding-right: 16px;
  color: rgb(20, 22, 26);
`

const Message = styled.p`
  margin-top: -5px;
  margin-bottom: 3px;
  font-size: 13px;
  color: rgb(20, 22, 26);
  line-height: 1.45;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  flex: 1 1 0%;
  min-height: 0px;
  max-height: 300px;
  margin-right: 0px;
  overflow: visible auto;
  scrollbar-width: none;
  box-sizing: border-box;

  a {
    color: rgb(20, 22, 26);
  }
`

const Meta = styled.span`
  font-size: 13px;
  line-height: 1.4;
  letter-spacing: 0px;
  color: rgb(108, 111, 116);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1);
`


interface IProps {
  message: ConversationMessage | null
  onClick: (message: ConversationMessage) => void
  onClose: () => void
  onHeightChange?: (height: number) => void
}

const NotificationPreview = (props: IProps) => {
  const { message, onHeightChange } = props
  const observerRef = useRef<ResizeObserver | null>(null)

  const containerRef = useCallback((node: HTMLDivElement | null) => {
    observerRef.current?.disconnect()
    observerRef.current = null
    if (!node || !onHeightChange) return
    observerRef.current = new ResizeObserver(entries => {
      for (const entry of entries) {
        onHeightChange(entry.contentRect.height)
      }
    })
    observerRef.current.observe(node)
  }, [onHeightChange])

  const onClick = () => {
    props.onClick(message)
  }

  const onCloseClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    props.onClose()
  }

  const authorName = message?.author?.name || message?.author?.first_name
  const authorAvatar = message?.author?.avatar?.url
  const time = moment(message?.created_at).fromNow()
  const meta = [authorName, time].filter(Boolean).join(' · ')

  if (!message) return null

  return (
    <Container ref={containerRef}>
      <Preview onClick={onClick}>
        <Avatar
          name={authorName}
          src={authorAvatar}
          width={32}
        />

        <MessageContentContainer>
          {message && <Message>
            <MessageContent
              message={message}
              onPreviewClick={() => { }}
              admin={false}
            />
          </Message>}
          <Meta>{meta}</Meta>
        </MessageContentContainer>

        <CloseButton onClick={onCloseClick}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M13.25 3.95L12.05 2.75L8 6.8L3.95 2.75L2.75 3.95L6.8 8L2.75 12.05L3.95 13.25L8 9.2L12.05 13.25L13.25 12.05L9.2 8L13.25 3.95Z" fill="currentColor"></path></svg>
        </CloseButton>
      </Preview>
    </Container>
  )
}

export default NotificationPreview
